import {
  ExError,
  SessionType,
  allSessionTypes,
  assertSameArrays,
  regexExtractCaptures,
} from '@shared';
import { FunctionComponent } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SessionLoader } from './components';
import {
  About,
  AdvisorClientAccessConflictPage,
  AdvisorDashboard,
  AdvisorOnboarding,
  AdvisorPublicInvite,
  AuthSignout,
  ClientHome,
  ClientPolicyApplication,
  DocusignCompletionPage,
  DocusignEmbeddedSigningPage,
  EmailPreferences,
  Home,
  Login,
  NotFound,
  OnboardingCarePhasesPhaseOne,
  OnboardingCarePhasesPhaseThree,
  OnboardingCarePhasesPhaseTwo,
  OnboardingCarePhasesSummary,
  OnboardingCareSettingsPhaseOne,
  OnboardingCareSettingsPhaseThree,
  OnboardingCareSettingsPhaseTwo,
  OnboardingCostFactors,
  OnboardingDownloadReport,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingFundingCoverage,
  OnboardingFundingSources,
  OnboardingOurPredictions,
  OnboardingPdfReport,
  OnboardingPolicyApplicationDashboard,
  OnboardingPredictedCareNeeds,
  OnboardingResultsAreIn,
  PolicyApplicationFormPage,
  RecordConsentsPage,
  RedirectHome,
  RedirectStripeCheckout,
  SeatInvitationResponse,
  Specs,
  WizardAdvisorHierarchy,
  WizardDataDump,
  OnboardingRedirect,
  OnboardingFormPage,
} from './pages';
import { LtcFinancialPlanning } from './pages/ltc-financial-planning/LtcFinancialPlanning';
import { isPublicOnboardingPath } from './hooks/useClientRoutes';

import {
  WithAuthenticationRequiredOptions,
  useAuth0,
  withAuthenticationRequired,
} from '@auth0/auth0-react';
import { selectSession } from '@client/model/selectors';
import { useSelector } from 'react-redux';
import { ClientRoute } from './components/ClientRoute';
import { UnauthorizedPage } from './components/UnauthorizedPage';
import { DuplicateAccountScreen } from './pages/DuplicateAccountScreen';
import { GoogleOAuthCallback } from './pages/GoogleOauthCallback';
import { MicrosoftOAuthCallback } from './pages/MicrosoftOauthCallback';
import { OnboardingPdfReportPrint } from './pages/OnboardingPdfReportPrint';
import { WizardAdvisorAdminPortal } from './pages/WizardAdvisorAdminPortal';

export type PageFn<TProps extends ObjectMap<string> | {} = {}> = FunctionComponent<TProps>;

export type QueryProps = { query: string };

type AppRouteSource = [PageFn, string];

export interface AppRoute {
  pageFn: PageFn;
  path: string;
  paramNames: string[];
  allowedSessionTypes: SessionType[];
}

export const unauthenticatedAppRoutes = (
  [
    [Login, '/'],
    [RedirectHome, '/auth/login'],
    [Specs, '/specs'],
    [EmailPreferences, '/email/preferences'],
    [AuthSignout, '/auth/signout'],
    [GoogleOAuthCallback, '/oauth/google/callback'],
    [MicrosoftOAuthCallback, '/oauth/microsoft/callback'],
    [SeatInvitationResponse, '/public/seat-invitations/:subscriptionSeatId/:invitedEmail/:action'],
    [SeatInvitationResponse, '/public/seat-invitations/:subscriptionSeatId/:action'],
    [AdvisorPublicInvite, '/onboard/:advisorId'],
  ] as AppRouteSource[]
).map(([pageFn, path]) => createAppRoute(pageFn, path, []));

export const authenticatedAppRoutes = (
  [
    [OnboardingResultsAreIn, '/clients/:clientId/onboarding/results-are-in'],
    [OnboardingOurPredictions, '/clients/:clientId/onboarding/our-predictions'],
    [OnboardingCostFactors, '/clients/:clientId/onboarding/cost-factors'],
    [OnboardingCarePhasesPhaseOne, '/clients/:clientId/onboarding/care-phases-early'],
    [OnboardingCarePhasesPhaseTwo, '/clients/:clientId/onboarding/care-phases-moderate'],
    [OnboardingCarePhasesPhaseThree, '/clients/:clientId/onboarding/care-phases-full'],
    [OnboardingCareSettingsPhaseOne, '/clients/:clientId/onboarding/care-settings-phase-one'],
    [OnboardingCareSettingsPhaseTwo, '/clients/:clientId/onboarding/care-settings-phase-two'],
    [OnboardingCareSettingsPhaseThree, '/clients/:clientId/onboarding/care-settings-phase-three'],
    [
      OnboardingFamilyCaregivingPhaseOne,
      '/clients/:clientId/onboarding/family-caregiving-phase-one',
    ],
    [
      OnboardingFamilyCaregivingPhaseTwo,
      '/clients/:clientId/onboarding/family-caregiving-phase-two',
    ],
    [
      OnboardingFamilyCaregivingPhaseThree,
      '/clients/:clientId/onboarding/family-caregiving-phase-three',
    ],
    [OnboardingPredictedCareNeeds, '/clients/:clientId/onboarding/predicted-care-needs'],
    [OnboardingCarePhasesSummary, '/clients/:clientId/onboarding/care-phases-summary'],
    [OnboardingFundingCoverage, '/clients/:clientId/onboarding/funding-coverage'],
    [OnboardingFundingSources, '/clients/:clientId/onboarding/funding-sources'],
    [OnboardingDownloadReport, '/clients/:clientId/onboarding/download-report'],
    [OnboardingPdfReport, '/clients/:clientId/onboarding/report'],
    [OnboardingPdfReportPrint, '/clients/:clientId/onboarding/report/print'],
    [
      OnboardingPolicyApplicationDashboard,
      '/clients/:clientId/onboarding/policy-application-dashboard',
    ],
    [
      DocusignEmbeddedSigningPage,
      '/clients/:clientId/embedded-signing/:clientPolicyApplicationId/:policyApplicationFormId',
    ],
    [DocusignCompletionPage, '/docusign/completion'],
    [ClientPolicyApplication, '/clients/:clientId/policy-application/:clientPolicyApplicationId'],
    [
      PolicyApplicationFormPage,
      '/clients/:clientId/policy-form/:clientPolicyApplicationId/:formId/:forClientId',
    ],
    [RecordConsentsPage, '/clients/:clientId/record-consents'],
    // LTC Financial Planning route (single route with query params)
    [LtcFinancialPlanning, '/clients/:clientId/onboarding/ltc-financial-planning'],
    [OnboardingFormPage, '/onboarding-form/:formReferenceId'],
  ] as AppRouteSource[]
).map(([pageFn, path]) =>
  createAppRoute(pageFn, path, [SessionType.advisor, SessionType.wizard, SessionType.client]),
);

export const authenticatedAdvisorOnlyAppRoutes = (
  [
    [RedirectStripeCheckout, '/auth/stripe-checkout'],
    [AdvisorDashboard, '/advisors/:advisorId'],
    [AdvisorDashboard, '/advisors'],
    [AdvisorDashboard, '/advisors/:advisorId/*'],
    [AdvisorOnboarding, '/advisors/:advisorId/onboarding/*'],
    [About, '/about'],
  ] as AppRouteSource[]
).map(([pageFn, path]) =>
  createAppRoute(pageFn, path, [
    SessionType.advisor,
    SessionType.wizard,
    SessionType.advisorInactiveSubscription,
    SessionType.advisorUnverifiedAccount,
    SessionType.advisorNoSubscription,
  ]),
);

export const wizardAppRoutes = (
  [
    [WizardDataDump, '/wizard/data-dump/clients/:clientId'],
    [WizardAdvisorHierarchy, '/wizard/advisor-hierarchy'],
    [WizardAdvisorAdminPortal, '/wizard/advisor-admin-portal'],
  ] as AppRouteSource[]
).map(([pageFn, path]) => createAppRoute(pageFn, path, [SessionType.wizard]));

export const appRoutes = [
  ...unauthenticatedAppRoutes,
  createAppRoute(OnboardingRedirect, '/onboarding/:formReferenceId', [
    SessionType.unauthenticated,
    SessionType.unknown,
    SessionType.client,
    SessionType.advisor,
  ]),
  ...authenticatedAppRoutes,
  ...wizardAppRoutes,
  ...authenticatedAdvisorOnlyAppRoutes,
  createAppRoute(Home, '/', allSessionTypes),
  createAppRoute(ClientHome, '/client/home', [
    SessionType.client,
    SessionType.unknown,
    SessionType.unauthenticated,
  ]),
  createAppRoute(AdvisorClientAccessConflictPage, '/advisor-client-conflict', allSessionTypes),
  createAppRoute(DuplicateAccountScreen, '/duplicate-account', [SessionType.duplicateAccount]),
];

export const appRoutesByPage = new Map<PageFn, AppRoute>();
rebuildAppRoutesByPage();

const ProtectedRoute = ({
  component,
  allowedSessionTypes,
  ...args
}: {
  component: PageFn;
  allowedSessionTypes: SessionType[];
} & WithAuthenticationRequiredOptions) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth0();
  const { sessionType } = useSelector(selectSession);
  const Component = withAuthenticationRequired(component, {
    ...args,
  });
  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  // Advisor trying to access client home
  if (
    isAdvisorSession &&
    window.location.pathname === '/client/home' &&
    searchParams.get('client_sign_in') === 'true'
  ) {
    const currentSearch = window.location.search;
    return (
      <Navigate
        to={`/advisor-client-conflict${currentSearch}`}
        replace
      />
    );
  }

  if (!allowedSessionTypes.includes(sessionType) && isAuthenticated) {
    return <UnauthorizedPage />;
  }

  return <Component {...params} />;
};

export function Router() {
  return (
    <SessionLoader>
      <Routes>
        {appRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={<RouteProxy {...route} />}
          />
        ))}
        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>
    </SessionLoader>
  );
}

function RouteProxy(route: AppRoute) {
  const params = useParams();
  const { allowedSessionTypes, pageFn: Component, path } = route;
  const isClientRoute = path.includes('/clients/:clientId');
  const isOnboardingRoute = isPublicOnboardingPath(path);

  if (allowedSessionTypes.length > 0) {
    if (isClientRoute) {
      return (
        <ClientRoute
          component={Component}
          allowedSessionTypes={allowedSessionTypes}
        />
      );
    }
    if (isOnboardingRoute) {
      return <Component {...params} />;
    }
    return (
      <ProtectedRoute
        component={Component}
        allowedSessionTypes={allowedSessionTypes}
      />
    );
  }
  return <Component {...params} />;
}

export function pageToUrl(page: PageFn<{}>): string;
export function pageToUrl(page: PageFn<{}>, targetProps: {} | undefined): string;
export function pageToUrl<TProps extends ObjectMap<string>>(
  page: PageFn<TProps>,
  props: TProps,
): string;
export function pageToUrl(page: PageFn<any>, props: ObjectMap<string> = {} as ObjectMap<string>) {
  const route =
    appRoutesByPage.get(page) ?? (rebuildAppRoutesByPage() && appRoutesByPage.get(page));
  if (route === undefined) {
    throw new ExError(
      'A development error occurred; attempted to link to a non-routed page. Add page to the path map in Router.',
      {
        targetPage: page.name,
        props,
        routedPages: Array.from(appRoutesByPage.entries()).map(([pageKey, { path }]) => ({
          pageKey: pageKey.name,
          path,
        })),
      },
    );
  }

  assertSameArrays(
    route.paramNames,
    Object.keys(props),
    'A development error occurred; attempted to link to a page with incorrect parameters.',
    {
      props,
      route,
    },
  );

  return route.path.replace(/\/:(\w+)/g, (_, propName) => `/${props[propName]}`);
}

export function useNavigateToPage() {
  const navigate = useNavigate();
  return navigateToPage;
  function navigateToPage(page: PageFn<{}>): void;
  function navigateToPage<TProps extends ObjectMap<string>>(
    page: PageFn<TProps>,
    props: TProps,
  ): void;
  function navigateToPage(page: PageFn, props: ObjectMap<string> = {} as ObjectMap<string>) {
    const url = pageToUrl(page, props);
    navigate(url);
  }
}

function rebuildAppRoutesByPage(): true {
  appRoutesByPage.clear();
  appRoutes.forEach(route => {
    const { pageFn } = route;
    if (!appRoutesByPage.has(pageFn)) {
      appRoutesByPage.set(pageFn, route);
    }
  });
  return true; // allows us to call as part of an expression..
}

function createAppRoute(pageFn: PageFn, path: string, allowedSessionTypes: SessionType[]) {
  return {
    pageFn,
    path,
    paramNames: regexExtractCaptures(path, /:(\w+)/g) ?? [],
    allowedSessionTypes,
  } as AppRoute;
}

export function getPublicRoutePaths(): string[] {
  return unauthenticatedAppRoutes
    .map(route => route.path)
    .map(path => {
      // Convert parameterized paths to base paths for prefix matching
      // e.g., '/public/seat-invitations/:id/:action' -> '/public/seat-invitations'
      const firstParamIndex = path.indexOf('/:');
      return firstParamIndex > 0 ? path.substring(0, firstParamIndex) : path;
    });
}
